import { CASE_STUDIES, type CaseStudy } from "@/data/caseStudies";

export type MaterialTag =
  | "sunbrella"
  | "phifertex"
  | "vinyl"
  | "aluminum"
  | "steel"
  | "stainless";

export const MATERIAL_LABELS: Record<MaterialTag, string> = {
  sunbrella: "Sunbrella® Canvas",
  phifertex: "Phifertex® Mesh",
  vinyl: "Heavy-Duty Vinyl",
  aluminum: "Welded Aluminum",
  steel: "Steel Frame",
  stainless: "Stainless Hardware",
};

const MATERIAL_KEYWORDS: Record<MaterialTag, RegExp> = {
  sunbrella: /sunbrella/i,
  phifertex: /phifer(tex)?/i,
  vinyl: /vinyl/i,
  aluminum: /aluminum|tig-welded/i,
  steel: /\bsteel\b/i,
  stainless: /stainless|316/i,
};

export function materialsFor(study: CaseStudy): MaterialTag[] {
  const haystack = study.specs
    .map((s) => `${s.label} ${s.value}`)
    .concat(study.scope, study.outcomes)
    .join(" \n ");
  const tags: MaterialTag[] = [];
  for (const [tag, rx] of Object.entries(MATERIAL_KEYWORDS) as [
    MaterialTag,
    RegExp,
  ][]) {
    if (rx.test(haystack)) tags.push(tag);
  }
  return tags;
}

export function citySlugFor(study: CaseStudy): string | undefined {
  if (!study.cityHref) return undefined;
  return study.cityHref.replace(/^\//, "");
}

export interface CaseFilterOptions {
  city?: string;
  category?: string;
  material?: MaterialTag;
}

export function filterCaseStudies(opts: CaseFilterOptions): CaseStudy[] {
  return CASE_STUDIES.filter((c) => {
    if (opts.city && citySlugFor(c) !== opts.city) return false;
    if (opts.category && c.category !== opts.category) return false;
    if (opts.material && !materialsFor(c).includes(opts.material)) return false;
    return true;
  });
}

export function availableCities(): { slug: string; label: string; count: number }[] {
  const map = new Map<string, { label: string; count: number }>();
  for (const c of CASE_STUDIES) {
    const slug = citySlugFor(c);
    if (!slug) continue;
    const label = c.location.replace(/, TX$/, "");
    const entry = map.get(slug) ?? { label, count: 0 };
    entry.count += 1;
    map.set(slug, entry);
  }
  return [...map.entries()]
    .map(([slug, v]) => ({ slug, ...v }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function availableCategories(): { value: string; count: number }[] {
  const map = new Map<string, number>();
  for (const c of CASE_STUDIES) {
    map.set(c.category, (map.get(c.category) ?? 0) + 1);
  }
  return [...map.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => a.value.localeCompare(b.value));
}

export function availableMaterials(): { tag: MaterialTag; label: string; count: number }[] {
  const map = new Map<MaterialTag, number>();
  for (const c of CASE_STUDIES) {
    for (const t of materialsFor(c)) {
      map.set(t, (map.get(t) ?? 0) + 1);
    }
  }
  return [...map.entries()]
    .map(([tag, count]) => ({ tag, label: MATERIAL_LABELS[tag], count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
